/** 기존 @gc/common/timezone - FE용 */
import moment from 'moment-timezone';

export const BACKEND_DEFAULT_TIMEZONE = 'UTC';

export function getCurrentTimezone(): string {
  return moment.tz.guess();
}

export function formatInTimezone(
  date: Date | string | number,
  timezone: string,
  format = 'YYYY-MM-DD HH:mm'
): string {
  return moment(date).tz(timezone).format(format);
}

/** 날짜를 "3 days ago (2025-01-15)" 형태로 변환 (원본 Mixins.toHumanizedDate) */
export function toHumanizedDate(
  date: Date | string | number | null | undefined,
  opts: {
    hideDate?: boolean;
    daysToPass?: number;
    timezone?: string;
  } = {}
): string {
  if (!date) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  const forHuman = m.fromNow();
  const daysPast = moment().diff(m, 'days');
  const { hideDate = false, daysToPass = 1, timezone = getCurrentTimezone() } = opts;
  if (daysPast > daysToPass && !hideDate) {
    return `${forHuman} (${formatInTimezone(m.toDate(), timezone, 'YYYY-MM-DD')})`;
  }
  return forHuman;
}

/** 날짜가 오늘인지 여부 */
export function isToday(dateStr?: string): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

/** 날짜 포맷, 오늘이면 ' N' 마크 추가 */
export function formatDateWithNew(iso?: string, format = 'YYYY-MM-DD HH:mm'): string {
  if (!iso) return '-';
  const m = moment(iso);
  if (!m.isValid()) return '-';
  const formatted = m.format(format);
  return m.isSame(moment(), 'day') ? `${formatted} N` : formatted;
}

/** 상대 날짜 (compact): "Today" / "1d ago" / "3d ago" / "2mo ago" / "1y ago" */
export function relativeDate(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = moment().diff(moment(dateStr), 'days');
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  if (diff < 30) return `${diff}d ago`;
  const months = Math.floor(diff / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/**
 * Relative time for a moment that may be in the *future* (unlike `relativeDate`,
 * which only handles the past and reads at day granularity) — hour-granular
 * phrasing for the past ("3 hours ago" / "2 days ago"), moment's own
 * "in 15 hours" style for the future. Meant to be appended in parens after an
 * absolute date, e.g. a scheduled "next update" time: `18:33 (in 15 hours)`.
 *
 * For the past, the day count is computed the *same way* `relativeDate`/
 * `updateFreshnessTier` do (`moment().diff(m, 'days')`, which truncates)
 * rather than via moment's own `.fromNow()` — that rounds instead of
 * truncating, so the exact same timestamp could show "2d ago" in the trigger
 * chip (`relativeDate`) but "3 days ago" here, a mismatch a user can catch by
 * comparing the two labels for the same field in the same popover.
 */
export function relativeFromNow(dateStr?: string): string {
  if (!dateStr) return '';
  const m = moment(dateStr);
  const now = moment();
  if (m.isAfter(now)) {
    // Future — nothing else in the UI computes a competing day-count to
    // disagree with, so moment's own rounded phrasing is fine as-is.
    return m.fromNow();
  }
  const days = now.diff(m, 'days');
  if (days >= 1) return days === 1 ? '1 day ago' : `${days} days ago`;
  const hours = now.diff(m, 'hours');
  if (hours >= 1) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  return m.fromNow(); // sub-hour: "a few seconds ago" / "a minute ago", etc.
}

/**
 * Freshness tier for a channel-stats "Updated At" timestamp, so the relative-time
 * label (`relativeDate`) can be color-coded at a glance instead of read closely:
 * `recent` (≤3 days) / `stale` (≤14 days) / `old` (>14 days). Boundaries match this
 * codebase's own automatic-update cadences — TikTok's 3-day cycle and Instagram's
 * 7-day cycle (see the "bypasses the normal N-day cycle" instant-update tooltips
 * in panel/user.tsx) — so `old` means "missed even the slowest normal cycle
 * twice over", not an arbitrary round number. Missing timestamps default to
 * `old` — no update history is the least fresh state, not neutral.
 */
export type UpdateFreshnessTier = 'recent' | 'stale' | 'old';
export function updateFreshnessTier(dateStr?: string): UpdateFreshnessTier {
  if (!dateStr) return 'old';
  const diff = moment().diff(moment(dateStr), 'days');
  if (diff <= 3) return 'recent';
  if (diff <= 14) return 'stale';
  return 'old';
}

/**
 * Resolves the "last updated" instant for one of the `${field}UpdatedAt` /
 * `${field}UpdatedAtStatus` / `${field}UpdatedOkAt` triples the BE stamps per
 * updatable field (GGC2-58 — see ggc-be's
 * docs/GGC2-58-channel-update-times-and-campaign-refresh.md). Prefers the last
 * *successful* refresh (`updatedOkAt`); falls back to `updatedAt` only when
 * that attempt's own status was `'ok'` (so a channel whose most recent attempt
 * errored doesn't show a stale-looking "success" time). `undefined` → never
 * successfully updated — callers should show a placeholder (e.g. `—`).
 */
export function resolveUpdatedOkAt(fields: {
  updatedAt?: string;
  updatedAtStatus?: string;
  updatedOkAt?: string;
}): string | undefined {
  return fields.updatedOkAt ?? (fields.updatedAtStatus === 'ok' ? fields.updatedAt : undefined);
}

/** 상대 날짜 (verbose): "today" / "3 days ago" / "2 months ago" / "1 year ago" */
export function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days < 1) return 'today';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} months ago`;
  return `${Math.floor(months / 12)} years ago`;
}

/**
 * 브라우저 로컬 타임존 기준 "YYYY-MM-DD" 또는 "YYYY-MM-DD HH:mm" 포맷.
 * iso 없음 → fallback. 파싱 실패 → onInvalid==='raw'면 원본 문자열 그대로, 아니면 fallback.
 */
export function formatLocalDateTime(
  iso?: string | null,
  opts?: { withTime?: boolean; fallback?: string; onInvalid?: 'fallback' | 'raw' },
): string {
  const { withTime = true, fallback = '-', onInvalid = 'fallback' } = opts ?? {};
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return onInvalid === 'raw' ? iso : fallback;
  const p = (n: number) => String(n).padStart(2, '0');
  const datePart = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return withTime ? `${datePart} ${p(d.getHours())}:${p(d.getMinutes())}` : datePart;
}

/** UTC 기준 "YYYY-MM-DD HH:mm" 포맷 (toISOString 기반, 로컬 타임존 무관). */
export function formatUtcDateTime(iso?: string, fallback = '-'): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? fallback : d.toISOString().slice(0, 16).replace('T', ' ');
}

/** duration(초)을 "2h 30m" 형태로 포맷. zeroAsFallback=true면 0초도 fallback 처리(원본 formatDuration 호환). */
export function formatDurationSeconds(
  sec: number | null | undefined,
  opts?: { fallback?: string; zeroAsFallback?: boolean },
): string {
  const { fallback = '-', zeroAsFallback = false } = opts ?? {};
  if (sec == null || !Number.isFinite(sec) || sec < 0) return fallback;
  if (zeroAsFallback && sec === 0) return fallback;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
}
